import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Icon } from '@/components/Icon';
import { RequireRole } from '@/components/RequireRole';
import {
  Banner,
  EmptyState,
  ErrorPage,
  LoadingPage,
  SectionTitle,
  SparkRuns,
  Stat,
  StatePill,
  StatusBadge,
  TestModeChip,
  TriggerChip,
  WorkflowLabelChips,
} from '@/components/primitives';
import { useAlerts, useRules, useWorkflows } from '@/hooks/useSturmData';
import type { Alert, WorkflowSummary } from '@/types/sturm';

export function WorkflowsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const queue = searchParams.get('queue');

  const { data: workflows, isLoading, isError, error } = useWorkflows();
  const { data: alerts = [] } = useAlerts();
  const { data: rules = [] } = useRules();

  const [term, setTerm] = useState('');
  const [app, setApp] = useState('all');
  const [health, setHealth] = useState('all');

  const all = useMemo(() => (workflows ?? []).filter((w) => !w.isDisabled), [workflows]);
  const disabledCount = (workflows?.length ?? 0) - all.length;

  const rows = all.filter(
    (w) =>
      (!queue || w.queue === queue) &&
      (app === 'all' || w.app === app) &&
      (health === 'all' || w.health === health) &&
      (!term ||
        `${w.name}${w.slug}${w.queue}${w.owner ?? ''}${w.labels.join(' ')}`
          .toLowerCase()
          .includes(term.toLowerCase())),
  );

  const totals = all.reduce(
    (acc, w) => ({
      running: acc.running + w.running,
      runs: acc.runs + w.runs24h,
      failed: acc.failed + w.failed24h,
      retries: acc.retries + w.retries24h,
    }),
    { running: 0, runs: 0, failed: 0, retries: 0 },
  );

  const attention = alerts.filter((a) => a.state === 'firing' || a.state === 'acknowledged');
  const firing = alerts.filter((a) => a.state === 'firing').length;

  if (isLoading) {
    return <LoadingPage label="Loading workflows…" />;
  }

  if (isError) {
    return (
      <ErrorPage
        title="Could not load workflows"
        message={error instanceof Error ? error.message : 'The API did not respond.'}
      />
    );
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">Workflows</div>
          <div className="page-sub">
            Durable executions grouped by the application that owns them. Every workflow carries its
            own runs, steps, settings, and notification rules.
          </div>
          {queue ? (
            <div className="row gap-8 mt-12">
              <span className="code-chip">
                <Icon name="layers" size={12} />
                {queue}
              </span>
              <button className="btn btn-xs btn-ghost" onClick={() => setSearchParams({})}>
                <Icon name="close" size={11} /> Clear queue filter
              </button>
            </div>
          ) : null}
        </div>
        <div className="page-actions">
          {disabledCount > 0 ? (
            <button className="btn btn-secondary" onClick={() => navigate('/settings')}>
              <Icon name="stop" size={14} /> {disabledCount} disabled
            </button>
          ) : null}
          <button className="btn btn-secondary" onClick={() => navigate('/notifications')}>
            <Icon name="bell" size={14} /> Notification rules
          </button>
        </div>
      </div>

      <div className="grid-4 mb-24">
        <Stat
          icon="play"
          label="Running now"
          value={totals.running.toLocaleString()}
          meta={`across ${all.length} workflow${all.length === 1 ? '' : 's'}`}
        />
        <Stat
          icon="refresh"
          label="Runs · 24h"
          value={totals.runs.toLocaleString()}
          meta={`${totals.retries.toLocaleString()} retries absorbed`}
        />
        <Stat
          icon="alert"
          label="Failed · 24h"
          value={totals.failed}
          tone={totals.failed ? 'error' : undefined}
          meta={describeConcentration(all)}
        />
        <Stat
          icon="bell"
          label="Alerts firing"
          value={firing}
          tone={firing ? 'error' : undefined}
          meta={`${rules.filter((r) => r.isEnabled).length} rules active`}
          onClick={() => navigate('/notifications')}
        />
      </div>

      {attention.length > 0 ? (
        <>
          <SectionTitle
            count={attention.length}
            right={
              <button className="btn btn-xs btn-ghost" onClick={() => navigate('/notifications')}>
                All notifications <Icon name="arrowRight" size={11} />
              </button>
            }
          >
            Needs attention
          </SectionTitle>
          <div className="att-grid">
            {attention.map((alert) => (
              <AttentionCard
                key={alert.id}
                alert={alert}
                workflowName={all.find((w) => w.slug === alert.workflowSlug)?.name ?? alert.workflowSlug ?? ''}
                onOpen={() => alert.workflowSlug && navigate(`/workflows/${alert.workflowSlug}`)}
              />
            ))}
          </div>
        </>
      ) : null}

      <div className="filterbar">
        <div className="search" style={{ width: 260 }}>
          <Icon name="search" size={14} />
          <input
            placeholder="Filter workflows, queues, owners…"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
          />
        </div>
        <select className="select" value={app} onChange={(e) => setApp(e.target.value)}>
          <option value="all">All applications</option>
          {[...new Set(all.map((w) => w.app))].map((a) => (
            <option key={a} value={a}>
              {a}
            </option>
          ))}
        </select>
        <select className="select" value={health} onChange={(e) => setHealth(e.target.value)}>
          <option value="all">Any health</option>
          <option value="failing">Failing</option>
          <option value="degraded">Degraded</option>
          <option value="healthy">Healthy</option>
          <option value="paused">Paused</option>
        </select>
        <div style={{ marginLeft: 'auto' }} className="tiny muted">
          {rows.length} of {all.length} workflows
        </div>
      </div>

      <div className="card" style={{ overflowX: 'auto' }}>
        {rows.length === 0 ? (
          <EmptyState
            title={all.length === 0 ? 'No workflows registered' : 'Nothing matches those filters'}
            sub={
              all.length === 0
                ? 'Workflows are discovered from code at startup. Deploy a build that declares one and it appears here.'
                : 'Try widening the application or health filter.'
            }
          />
        ) : (
          <table className="tbl" style={{ minWidth: 1040 }}>
            <thead>
              <tr>
                <th>Workflow</th>
                <th>Trigger</th>
                <th style={{ width: 190 }}>Last 40 runs</th>
                <th style={{ textAlign: 'right' }}>Runs 24h</th>
                <th style={{ textAlign: 'right' }}>Failed</th>
                <th style={{ textAlign: 'right' }}>Success</th>
                <th style={{ textAlign: 'right' }}>p95</th>
                <th style={{ textAlign: 'right' }}>Running</th>
                <th>Health</th>
                <th style={{ textAlign: 'right' }}>Alerts</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((w) => (
                <tr key={w.slug} className="clickable" onClick={() => navigate(`/workflows/${w.slug}`)}>
                  <td>
                    <div className="row gap-10">
                      <div className="tile" style={{ width: 28, height: 28, borderRadius: 6 }}>
                        <Icon name="flow" size={14} />
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div className="row gap-6">
                          <div className="wf-name">{w.name}</div>
                          {w.isTestMode ? <TestModeChip /> : null}
                          <WorkflowLabelChips labels={w.labels} />
                        </div>
                        <div className="wf-queue">
                          {w.app} · {w.queue}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td>
                    <TriggerChip trigger={w.trigger} />
                  </td>
                  <td>
                    <SparkRuns statuses={w.sparkline} height={24} />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span className="wf-num">{w.runs24h.toLocaleString()}</span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span className={`wf-num ${w.failed24h ? 'bad' : 'dim'}`}>{w.failed24h}</span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span className="wf-num">{w.successRate}%</span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span className="wf-num">{w.p95 ?? '—'}</span>
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span className={`wf-num ${w.running ? '' : 'dim'}`}>{w.running.toLocaleString()}</span>
                  </td>
                  <td>
                    <StatusBadge status={w.isPaused ? 'paused' : w.health} />
                  </td>
                  <td style={{ textAlign: 'right' }}>
                    <span
                      className="row gap-5"
                      style={{
                        justifyContent: 'flex-end',
                        color: w.ruleCount ? 'var(--ink-700)' : 'var(--ink-300)',
                      }}
                    >
                      <Icon name={w.ruleCount ? 'bell' : 'bellOff'} size={13} />
                      <span className="mono tiny">{w.ruleCount || '—'}</span>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <RequireRole min="admin">
        <div className="mt-24">
          <Banner>
            Workflow definitions come from the assemblies Sturm loads at startup. Changing a step&apos;s
            shape starts a new version — in-flight runs finish on the version they began.
          </Banner>
        </div>
      </RequireRole>
    </div>
  );
}

function AttentionCard({
  alert,
  workflowName,
  onOpen,
}: {
  alert: Alert;
  workflowName: string;
  onOpen: () => void;
}) {
  return (
    <div className={`att sev-${alert.severity}`} onClick={onOpen}>
      <div className="flex1" style={{ minWidth: 0 }}>
        <div className="att-t">{alert.title}</div>
        <div className="att-b">{alert.body}</div>
        <div className="att-m">
          <span className="row gap-6">
            <Icon name="flow" size={12} />
            {workflowName}
          </span>
          <StatePill state={alert.state} by={alert.acknowledgedBy} />
          <span style={{ marginLeft: 'auto' }}>{alert.at}</span>
        </div>
      </div>
    </div>
  );
}

function describeConcentration(workflows: WorkflowSummary[]): string {
  const failing = workflows.filter((w) => w.failed24h > 0).sort((a, b) => b.failed24h - a.failed24h);
  const total = failing.reduce((sum, w) => sum + w.failed24h, 0);

  if (total === 0) {
    return 'no failures in the window';
  }

  const topTwo = failing.slice(0, 2).reduce((sum, w) => sum + w.failed24h, 0);
  const share = Math.round((topTwo / total) * 100);

  return failing.length <= 2
    ? `${failing.length} workflow${failing.length === 1 ? '' : 's'} affected`
    : `2 workflows account for ${share}%`;
}
